import { useGames } from '@ottd/api';
import type { GameType } from '@ottd/api';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { FilterTabs } from '@/components/prototype/filter-tabs';
import type { TypeFilter } from '@/components/prototype/filter-tabs';
import { PageHeader } from '@/components/prototype/page-header';
import { DefaultSkeleton, QueryState } from '@/components/prototype/query-state';
import { Pagination } from '@/components/ui/pagination';
import { readPage, withPage } from '@/lib/page-param';
import { GameHistoryCard } from '@/pages/games/game-history-card';

/** The old site's page param, so inbound legacy links keep their page. */
const LEGACY_PAGE = 'page_games';

/** Legacy `/game?all=1` lists every status, not just ended games. */
const ALL_STATUSES = 'all';

function parseType(raw: string | null): TypeFilter {
  return raw === 'CB' || raw === 'CV' ? raw : 'all';
}

/** PHP truthiness, as the legacy controller read it: `0` or empty is off. */
function parseAll(raw: string | null): boolean {
  return raw !== null && raw !== '' && raw !== '0';
}

export default function GamesPage() {
  const { t } = useTranslation('game');
  const [params, setParams] = useSearchParams();
  const type = parseType(params.get('type'));
  const page = readPage(params, LEGACY_PAGE);
  const all = parseAll(params.get(ALL_STATUSES));

  const apiType: GameType | undefined = type === 'all' ? undefined : type;
  const { data, isLoading, isError, error } = useGames({ type: apiType, page, all });

  const setType = (next: TypeFilter) => {
    const nextParams = withPage(params, 1, LEGACY_PAGE);
    if (next === 'all') nextParams.delete('type');
    else nextParams.set('type', next);
    setParams(nextParams);
  };

  const setPage = (next: number) => {
    setParams(withPage(params, next, LEGACY_PAGE));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="mx-auto max-w-[1080px] px-4 py-8 sm:px-5">
      <PageHeader eyebrow={t('list.eyebrow')} title={t('list.title')} />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <FilterTabs value={type} onChange={setType} />
        {data ? (
          <span className="text-dim ml-auto font-mono text-xs">
            {t('list.tracked', { count: data.meta.total })}
          </span>
        ) : null}
      </div>

      <div className="mt-4">
        <QueryState
          isLoading={isLoading}
          isError={isError}
          error={error}
          isEmpty={data?.data.length === 0}
          emptyLabel={t('list.empty')}
          skeleton={<DefaultSkeleton rows={5} />}
        >
          <div className="flex flex-col gap-3.5">
            {data?.data.map((game) => (
              <GameHistoryCard key={game.id} game={game} />
            ))}
          </div>
          {data ? (
            <Pagination
              className="mt-6"
              page={data.meta.current_page}
              lastPage={data.meta.last_page}
              onPageChange={setPage}
            />
          ) : null}
        </QueryState>
      </div>
    </div>
  );
}
